import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BuildingLibraryIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../utils/baseUtils';
import { cn } from '../../../components/ui/_cn';
import FinButton from './fin-button';
import { FinSkeletonLine } from './fin-skeleton';
import FinInfoPopover from './fin-info-popover';
import HeroAmount from './hero-amount';
import { describeSchedule } from './connect-schedule';

dayjs.extend(utc);

const DASH = '—';

// The balance card Overview draws at the top of the page (spec 2026-09-21
// §4.1): the hero numeral and the actions on the left, the rows under them,
// and a recessed rail on the right answering when, on what cadence, and to
// which account. Overview v3 (2026-10-11): the card shows Total Balance =
// In transit + Processing; Available and Pending are gone from it.
//
// Round 2 (rail correction, 2026-09-22): this component is Overview-only
// now — Payouts draws Stripe's own `<ConnectBalances/>` full width instead
// of this hero (payouts.js). What used to be "Pay out" is Stripe's own
// `<ConnectInstantPayoutsPromotion/>`, which Overview draws under this card
// (v3, frame 1a): it decides instant vs standard and whether this account is
// eligible at all, because it carries the losses on that decision and this
// component does not. The card's one action is "Edit" beside the Schedule
// row (owner, 2026-10-20: restaurants edit their payout schedule again —
// here through Overview's PayoutScheduleModal, on Payouts through Stripe's
// own card, and in the Manager app); the bank has its own way in through
// the account strip below.
//
// What it refuses to do, and why:
// - Never invents a next-payout date: the "Next payout" row reads
//   `balance.upcomingGroups[0]` — the money Stripe has actually grouped by
//   an expected arrival day — never a date computed from the schedule.
// - Never prints "$0.00" beside a real date: the Est. amount line needs
//   both the date and the amount.
// - Never draws a way to the bank, nor the schedule editor, for a viewer
//   the Payouts route refuses (`canManagePayouts`): the accountant reads the
//   money and stops there.
// - Draws the API's own `totalBalance`, `inTransit` and `processing`; the
//   only sum made here is the rail's money-in-no-payout-yet check.
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const Strong = ({ children }) => <strong className="font-bold text-fin-ink">{children}</strong>;
const TipTitle = ({ dot, children }) => (
  <span className="flex items-center gap-2 text-[15px] font-extrabold text-fin-ink">
    <span className={cn('h-2 w-2 flex-none rounded-full', dot)} aria-hidden="true" />
    {children}
  </span>
);

export const InTransitTip = () => (
  <>
    <TipTitle dot="bg-fin-blue">In transit</TipTitle>
    <span className="mt-2 block">
      This money has already been sent to your bank and is on its way. It usually arrives within{' '}
      <Strong>2 business days</Strong>. Weekends and bank holidays are not counted.
    </span>
    <span className="mt-2 block">
      Arrival time can vary by bank. Once it lands in your account, the payout is marked <Strong>Paid</Strong>.
    </span>
  </>
);

// The delay is read, not assumed (owner, 2026-10-07): 2 on every account
// today, which is the md's sentence word for word; without one, no number.
export const ProcessingTip = ({ days }) => {
  const delay = typeof days === 'number' ? plural(days, 'business day') : null;
  return (
    <>
      <TipTitle dot="bg-fin-faintest">Processing</TipTitle>
      <span className="mt-2 block">
        {delay
          ? `Processing funds become available ${delay} after a payment is received. Weekends and bank holidays are not counted.`
          : 'Processing funds become available once a payment has cleared.'}
      </span>
      <span className="mt-3.5 block font-bold text-fin-ink">New Stripe Accounts?</span>
      <span className="mt-1 block">
        Your first payout may take <Strong>7–14 days</Strong> after your first order due to Stripe&apos;s initial
        payout policy.
        {delay ? (
          <>
            {' '}After that, funds typically become available within <Strong>{delay}</Strong>.
          </>
        ) : null}
      </span>
    </>
  );
};

const Label = ({ children }) => (
  <p className="text-[12.5px] font-medium text-fin-muted">{children}</p>
);

const ErrorRow = ({ error, onRetry, className }) => (
  <div className={cn('flex items-center justify-between gap-3', className)}>
    <p className="text-[13.5px] text-fin-red">{error}</p>
    <FinButton onClick={onRetry} aria-label="Retry balance">
      Retry
    </FinButton>
  </div>
);

const ConnectBalanceHero = ({
  balance,
  loading = false,
  error = '',
  onRetry,
  canManagePayouts = false,
  onManage,
  onEditSchedule,
}) => {
  // No overflow-hidden: the card must not clip the tooltips, so the rail
  // rounds its own corners instead.
  const card = 'rounded-xl border border-fin-border bg-fin-surface';

  if (error && !balance) {
    return (
      <section className={card} aria-busy={loading}>
        <ErrorRow error={error} onRetry={onRetry} className="px-8 py-5" />
      </section>
    );
  }

  // Loading with no balance yet is the first fetch: nothing has landed, so
  // nothing should look like it has. Once a balance exists, a later refresh
  // keeps showing it — loading only adds aria-busy, not a wiped screen.
  const firstLoad = loading && !balance;
  const money = (value) => (firstLoad ? DASH : formatCurrency(value || 0));
  // Money in no payout yet: what the rail's below-zero branch keys on (ruling 12).
  const unpaid = (balance?.available || 0) + (balance?.pending || 0);

  const showButtons = canManagePayouts && !firstLoad;

  // A manual schedule sends nothing by itself, so no date is promised there
  // even while money waits (owner report 2026-09-23: the rail read "Next
  // payout Mon, Sep 28" right after the switch to Manual).
  const nextGroup =
    balance?.scheduleInterval === 'manual' ? null : balance?.upcomingGroups?.find((group) => group.amount > 0) || null;

  return (
    // The left column never grows past its share (`minmax(0, 1fr)`), so the
    // rail is never pushed out of the card at 1024px (owner report
    // 2026-10-10).
    <section
      data-testid="balance-card"
      className={cn(card, 'grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]')}
      aria-busy={loading}
    >
      <div className="px-8 pb-6 pt-7">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-fin-muted">Total Balance</p>
          <div className="mt-2.5 min-w-0 max-w-full">
            {firstLoad ? (
              <p className="text-[52px] font-bold leading-none text-fin-faintest">{DASH}</p>
            ) : (
              <HeroAmount amount={balance?.totalBalance || 0} currency={balance?.currency} showCurrency={false} />
            )}
          </div>
        </div>

        <dl className="mt-[26px]">
          <div className="grid grid-cols-[1fr_auto] items-center border-t border-fin-hair py-[11px] text-[13.5px]">
            <dt className="flex items-center gap-2.5">
              <span className="h-2 w-2 flex-none rounded-full bg-fin-blue" aria-hidden="true" />
              <span className="font-medium text-fin-ink">In transit</span>
              <FinInfoPopover variant="card" ariaLabel="About money in transit" text={<InTransitTip />} />
            </dt>
            <dd className="font-semibold tabular-nums text-fin-ink">{money(balance?.inTransit)}</dd>
          </div>
          <div className="grid grid-cols-[1fr_auto] items-center border-t border-fin-hair py-[11px] text-[13.5px]">
            <dt className="flex items-center gap-2.5">
              <span className="h-2 w-2 flex-none rounded-full bg-fin-faintest" aria-hidden="true" />
              <span className="font-medium text-fin-ink">Processing</span>
              <FinInfoPopover
                variant="card"
                ariaLabel="About processing funds"
                text={<ProcessingTip days={balance?.scheduleDelayDays} />}
              />
            </dt>
            <dd className="font-semibold tabular-nums text-fin-muted-2">{money(balance?.processing)}</dd>
          </div>
          <div className="grid grid-cols-[1fr_auto] items-center border-t border-fin-border-2 pb-0 pt-3 text-[13.5px]">
            <dt className="flex items-center gap-2.5">
              <span className="h-2 w-2" aria-hidden="true" />
              <span className="font-extrabold text-fin-ink">Total Balance</span>
            </dt>
            <dd data-testid="balance-total" className="font-extrabold tabular-nums text-fin-ink">
              {firstLoad ? DASH : formatCurrency(balance?.totalBalance || 0)}
            </dd>
          </div>
        </dl>

        {error ? <ErrorRow error={error} onRetry={onRetry} className="mt-3 border-t border-fin-hair pt-3" /> : null}
      </div>

      <aside className="flex flex-col gap-5 rounded-b-xl border-t border-fin-border bg-fin-recess px-7 py-[26px] lg:rounded-bl-none lg:rounded-r-xl lg:border-l lg:border-t-0">
        <div>
          {firstLoad ? <FinSkeletonLine className="h-3 w-20" /> : <Label>Next payout</Label>}
          {firstLoad ? (
            <div className="mt-2 space-y-2">
              <FinSkeletonLine className="h-5 w-32" />
              <FinSkeletonLine className="h-3 w-40" />
            </div>
          ) : nextGroup ? (
            <>
              <p className="mt-[5px] text-[20px] font-bold tracking-[-0.02em] text-fin-ink">
                {dayjs.utc(nextGroup.arrivalDate).format('ddd, MMM D')}
              </p>
              <p className="mt-1 text-[13px] text-fin-muted">
                Est. amount{' '}
                <span className="font-semibold tabular-nums text-fin-ink">
                  {formatCurrency(nextGroup.amount)}
                </span>
                {' · estimated'}
              </p>
            </>
          ) : unpaid < 0 ? (
            // Owner report 2026-09-23: after a refund took Starbucks to
            // -$1.26 the rail promised a "-$1.26" payout. Stripe sends none
            // below zero; it takes the shortfall from the next sales, or
            // debits the bank when there are none.
            <>
              <p className="mt-[5px] text-[14px] font-semibold text-fin-ink-2">Nothing to pay out</p>
              <p className="mt-1 text-[13px] text-fin-muted">
                Your balance is below zero, usually because of a refund. Stripe takes it from your next sales, or from
                your bank account if there are none.
              </p>
            </>
          ) : balance?.scheduleInterval === 'manual' ? (
            <p className="mt-[5px] text-[14px] font-semibold text-fin-ink-2">Manual payouts</p>
          ) : (
            <p className="mt-[5px] text-[14px] font-semibold text-fin-ink-2">No payout scheduled</p>
          )}
        </div>

        <div className="border-t border-fin-border" />

        <div>
          {firstLoad ? null : (
            <div className="flex items-baseline justify-between gap-3">
              <Label>Schedule</Label>
              {/* Drawn as soon as a balance is on screen, interval named or
                  not: a restaurant Stripe gave no schedule is the one that
                  needs the editor (spec 2026-10-20 §2.3). */}
              {showButtons && balance ? (
                <button
                  type="button"
                  onClick={onEditSchedule}
                  data-testid="payout-schedule-edit"
                  aria-label="Edit payout schedule"
                  className="-my-1 -mr-1 rounded px-1 py-1 text-[12.5px] font-semibold text-fin-blue hover:text-fin-blue-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fin-ring"
                >
                  Edit
                </button>
              ) : null}
            </div>
          )}
          {firstLoad ? (
            <FinSkeletonLine className="h-3 w-24" />
          ) : (
            <p className="mt-1 text-[13.5px] font-semibold text-fin-ink">{describeSchedule(balance)}</p>
          )}
        </div>

        {!firstLoad && balance?.bankName ? (
          <div>
            <Label>Destination</Label>
            <div className="mt-1.5 flex items-center gap-2.5 text-[13.5px]">
              <span className="flex h-7 w-7 items-center justify-center rounded-[7px] border border-fin-border bg-fin-surface">
                <BuildingLibraryIcon className="h-4 w-4 text-fin-muted" aria-hidden="true" />
              </span>
              {/* The name on its own line, the number under it (owner,
                  2026-10-06): side by side, a long bank name wrapped and
                  pushed the number into a second column of its own. Same two
                  lines as the To cell of the payout tables. */}
              <span className="min-w-0">
                <span className="block break-words font-semibold text-fin-ink">{balance.bankName}</span>
                {balance.bankLast4 ? (
                  <span className="mt-px block text-[12px] font-medium text-fin-muted">{`•••• ${balance.bankLast4}`}</span>
                ) : null}
              </span>
            </div>
          </div>
        ) : null}

        {showButtons ? (
          <button
            type="button"
            onClick={onManage}
            className="mt-auto inline-flex items-center gap-1 self-end text-[13px] font-semibold text-fin-blue hover:text-fin-blue-hover"
          >
            View all payouts
            <ChevronRightIcon className="h-3 w-3 stroke-[2.5]" aria-hidden="true" />
          </button>
        ) : null}
      </aside>
    </section>
  );
};

export default ConnectBalanceHero;
